import { useEffect, useState, FormEvent } from 'react';
import { Plus, Pencil, Trash2, AlertCircle, Users, X } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { Permission, Role } from '@/types';

type FormState = { name: string; label: string; description: string; permissions: Set<string> };
const emptyForm = (): FormState => ({ name: '', label: '', description: '', permissions: new Set() });

// ─── Permission checkbox group ────────────────────────────────────────────────

function PermissionCheckboxes({
  available,
  selected,
  onChange,
}: {
  available: Permission[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  function toggle(name: string) {
    const next = new Set(selected);
    next.has(name) ? next.delete(name) : next.add(name);
    onChange(next);
  }

  if (available.length === 0) {
    return (
      <p className="text-sm text-amber-600 dark:text-amber-400">
        No permissions defined yet — add permissions first on the Permissions page.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1">
      {available.map((p) => (
        <label
          key={p.name}
          className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors text-sm ${
            selected.has(p.name)
              ? 'bg-brand-50 dark:bg-brand-900/30 border-brand-300 dark:border-brand-700'
              : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
          }`}
        >
          <input
            type="checkbox"
            className="mt-0.5 flex-shrink-0 accent-brand-600"
            checked={selected.has(p.name)}
            onChange={() => toggle(p.name)}
          />
          <div className="min-w-0">
            <div className="font-mono text-xs text-gray-900 dark:text-white truncate">{p.name}</div>
            {p.label && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{p.label}</div>
            )}
          </div>
        </label>
      ))}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function RoleModal({
  mode,
  initial,
  permissions,
  onSave,
  onClose,
}: {
  mode: 'add' | 'edit';
  initial: FormState;
  permissions: Permission[];
  onSave: (form: FormState) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm]   = useState<FormState>(initial);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await onSave(form);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to save role.');
      setSaving(false);
    }
  }

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {mode === 'add' ? 'Create role' : 'Edit role'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">
                Role key <span className="text-red-500">*</span>
              </label>
              <input
                className="input font-mono"
                placeholder="doctor"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                disabled={mode === 'edit'}
                required
                autoFocus={mode === 'add'}
              />
              {mode === 'add' && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Lowercase letters and underscores. Cannot be changed later.
                </p>
              )}
            </div>
            <div>
              <label className="label">Label</label>
              <input
                className="input"
                placeholder="Doctor"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                autoFocus={mode === 'edit'}
              />
            </div>
          </div>

          <div>
            <label className="label">Description</label>
            <input
              className="input"
              placeholder="Clinical doctor with patient management access"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>

          <div>
            <label className="label mb-2">Permissions</label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Select which permissions this role grants. The Test Panel uses these to filter visible workflows.
            </p>
            <PermissionCheckboxes
              available={permissions}
              selected={form.permissions}
              onChange={(next) => setForm((f) => ({ ...f, permissions: next }))}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertCircle size={14} /> {error}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center gap-2 px-6 py-4 border-t border-gray-100 dark:border-gray-700">
          <button
            type="button"
            onClick={handleSubmit as unknown as React.MouseEventHandler}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? 'Saving…' : mode === 'add' ? 'Create role' : 'Save changes'}
          </button>
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

import React from 'react';

export function Roles() {
  const [roles, setRoles]           = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  const [modal, setModal] = useState<
    | { mode: 'add'; initial: FormState }
    | { mode: 'edit'; id: number; initial: FormState }
    | null
  >(null);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    setError(null);
    try {
      const [r, p] = await Promise.all([api.listRoles(), api.listPermissions()]);
      setRoles(r);
      setPermissions(p);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function openAdd() {
    setModal({ mode: 'add', initial: emptyForm() });
  }

  function openEdit(role: Role) {
    setModal({
      mode: 'edit',
      id: role.id,
      initial: {
        name: role.name,
        label: role.label,
        description: role.description,
        permissions: new Set(role.permissions),
      },
    });
  }

  async function handleSave(form: FormState) {
    const perms = Array.from(form.permissions);
    if (modal?.mode === 'add') {
      const created = await api.createRole({
        name: form.name.trim(),
        label: form.label.trim(),
        description: form.description.trim(),
        permissions: perms,
      });
      setRoles((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
    } else if (modal?.mode === 'edit') {
      const updated = await api.updateRole(modal.id, {
        label: form.label.trim(),
        description: form.description.trim(),
        permissions: perms,
      });
      setRoles((prev) => prev.map((r) => (r.id === modal.id ? updated : r)));
    }
    setModal(null);
  }

  async function confirmDelete() {
    if (deleteId === null) return;
    setDeleting(true);
    try {
      await api.deleteRole(deleteId);
      setRoles((prev) => prev.filter((r) => r.id !== deleteId));
      setDeleteId(null);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to delete.');
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="max-w-5xl">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Roles</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            Define roles and map permissions to them. The Test Panel uses these roles to simulate access.
          </p>
        </div>
        <button onClick={openAdd} className="btn-primary self-start sm:self-auto">
          <Plus size={16} className="mr-2" /> Add role
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-400 text-sm mb-4">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}

      {loading && <div className="text-sm text-gray-500 dark:text-gray-400 py-16 text-center">Loading…</div>}

      {/* Empty state */}
      {!loading && roles.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-16 text-center">
          <Users size={40} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="font-semibold text-gray-900 dark:text-white mb-1">No roles defined</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Create roles and map permissions so the Test Panel can simulate different user types.
          </p>
          <button onClick={openAdd} className="btn-primary">
            <Plus size={15} className="mr-1.5" /> Add first role
          </button>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 max-w-sm w-full shadow-xl">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Delete role?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              Deleting <strong>{roles.find((r) => r.id === deleteId)?.label || roles.find((r) => r.id === deleteId)?.name}</strong> removes its permission mappings.
              Workflows are not affected.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteId(null)} className="btn-secondary" disabled={deleting}>Cancel</button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50 transition-colors"
              >
                <Trash2 size={14} /> {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Roles table */}
      {!loading && roles.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                <th className="px-5 py-3 text-left">Role</th>
                <th className="px-5 py-3 text-left hidden sm:table-cell">Label</th>
                <th className="px-5 py-3 text-left">Permissions</th>
                <th className="px-5 py-3 text-right w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
              {roles.map((role) => (
                <tr key={role.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="font-mono text-gray-900 dark:text-white">{role.name}</div>
                    {role.description && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 max-w-[180px] truncate">
                        {role.description}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4 hidden sm:table-cell text-gray-700 dark:text-gray-300">
                    {role.label || <span className="text-gray-400 dark:text-gray-500 italic">—</span>}
                  </td>
                  <td className="px-5 py-4">
                    {role.permissions.length === 0 ? (
                      <span className="text-xs text-gray-400 dark:text-gray-500 italic">No permissions</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {role.permissions.map((p) => (
                          <span
                            key={p}
                            className="inline-block px-2 py-0.5 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400 border border-brand-200 dark:border-brand-700 rounded text-[11px] font-mono"
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(role)}
                        className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteId(role.id)}
                        className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <RoleModal
          mode={modal.mode}
          initial={modal.initial}
          permissions={permissions}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
